import type { Optional } from '../../../types.js';
import { groupBy } from '../../../visualizations/common/groupBy.js';
import { optionalMin, optionalMax, precisionRound } from '../../common/math.js';
import type { seriesTypeFlatDataset, seriesTypeFlatDatasetEntry } from '../render-groups/types.js';
import type { series } from '../types.js';
import type { ColumnStackedRenderGroup, columnStackedRenderGroupData } from './types.js';
export const getColumnStackedRenderGroups = (dataset: seriesTypeFlatDataset): ColumnStackedRenderGroup[] => {
  const keyGroupings = groupBy(dataset, x => x.datapoint.key, x => x.datapoint.value != null);
  const yTargetGroupings = Object.values(groupBy(dataset, x => `${x.series.yTarget ?? 'left'}:${x.datapoint.key}`, x => x.datapoint.value != null));
  const renderGroups: ColumnStackedRenderGroup[] = [];
  for (const yTargetGrouping of yTargetGroupings) {
    const data: columnStackedRenderGroupData[] = [];
    let minValue: Optional<number>;
    let maxValue: Optional<number>;
    let previousValue = 0;
    const appendData = (entry: seriesTypeFlatDatasetEntry) => {
      const newValue = precisionRound(previousValue + (entry.datapoint.value ?? 0));
      minValue = optionalMin(minValue, newValue);
      maxValue = optionalMax(maxValue, newValue);
      const keyIndex = keyGroupings[entry.datapoint.key].indexOf(entry);
      data.push({
        value: newValue,
        previousValue,
        color: entry.series.color,
        name: entry.series.name,
        showPointLabel: getShowPointLabel(entry.series),
        keyIndex
      });
      previousValue = newValue;
    };
    for (const entry of yTargetGrouping.filter(x => x.datapoint.value >= 0)) {
      appendData(entry);
    }
    previousValue = 0;
    for (const entry of yTargetGrouping.filter(x => x.datapoint.value < 0)) {
      appendData(entry);
    }
    const isDualYTargetContext = yTargetGroupings.some(g => g[0].series.yTarget !== 'right') && yTargetGroupings.some(g => g[0].series.yTarget === 'right');
    const entry = yTargetGrouping[0];
    renderGroups.push({
      type: 'column-stacked',
      yTarget: entry.series.yTarget ?? 'left',
      key: entry.datapoint.key,
      data,
      minValue,
      maxValue,
      isDualYTargetContext
    });
  }
  return renderGroups;
};
const getShowPointLabel = (series: series) => series.showPointLabels !== false;